import { FC } from 'react';
import { Button } from "@/components/ui/button";
import { CreditCard, Stars, Clock, Calendar, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useStripePayment } from '@/hooks/useStripePayment';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getPremiumStatus } from '@/utils/subscription';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';

export const SubscriptionInfo: FC = () => {
  const { user, profile, loading } = useAuth();
  const { handlePayment, isLoading } = useStripePayment();

  const handleSubscribe = async () => {
    if (!user?.id) {
      toast.error('Debes iniciar sesión para continuar');
      return;
    }

    try {
      await handlePayment(user.id);
    } catch (error) {
      console.error('Error al procesar el pago:', error);
      toast.error('Error al procesar el pago. Por favor intenta de nuevo.');
    }
  };

  const getRemainingTrialDays = (trialStartDate: string | null): number => {
    if (!trialStartDate) return 7;
    
    const startDate = new Date(trialStartDate);
    const now = new Date();
    const trialDuration = 7; // 7 días de prueba
    
    // Si el usuario se acaba de registrar, devolver 7 días
    if (startDate.getTime() > now.getTime()) return 7;
    
    const elapsedDays = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, trialDuration - elapsedDays);
    
    // Si el usuario se registró hoy, devolver 7 días
    if (elapsedDays === 0) return 7;
    
    return remainingDays;
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center p-8">
        <Stars className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const subscriptionStatus = getPremiumStatus(profile);
  const isPremium = subscriptionStatus.type === 'premium';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-semibold tracking-tight">Plan Actual</h2>
        <p className="text-sm text-muted-foreground">
          Gestiona tu suscripción y método de pago
        </p>
      </div>

      <div className="grid gap-4">
        <div className="flex flex-col space-y-4 p-6 border rounded-lg bg-gradient-to-br from-white to-gray-50">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                {isPremium ? (
                  <>
                    <Stars className="h-5 w-5 text-yellow-500" />
                    <h3 className="font-medium text-lg">Plan Premium</h3>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 text-gray-500" />
                    <h3 className="font-medium text-lg">Plan Free</h3>
                  </>
                )}
              </div>
              
              <div className="flex items-center gap-1 text-sm">
                {subscriptionStatus.type === 'trial' && (
                  <div className="text-blue-600">
                    <Clock className="inline-block h-4 w-4 mr-1" />
                    {getRemainingTrialDays(profile.trial_start_date)} días restantes de prueba gratuita
                  </div>
                )}
                
                {subscriptionStatus.type === 'grace' && (
                  <div className="text-orange-600">
                    <AlertTriangle className="inline-block h-4 w-4 mr-1" />
                    {subscriptionStatus.daysRemaining} días para activar Premium antes de que expire tu cuenta
                  </div>
                )}
                
                {subscriptionStatus.type === 'expired' && (
                  <div className="text-red-600">
                    <AlertTriangle className="inline-block h-4 w-4 mr-1" />
                    Tu cuenta ha expirado. Activa Premium para continuar usando la plataforma
                  </div>
                )}
              </div>
            </div>

            {!isPremium && (
              <Button
                onClick={handleSubscribe}
                className="bg-gradient-to-r from-primary via-secondary to-accent text-white hover:opacity-90 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Stars className="h-4 w-4 animate-spin" />
                    <span>Procesando...</span>
                  </div>
                ) : (
                  'Activar Premium'
                )}
              </Button>
            )}
          </div>

          {isPremium && profile.subscription_start_date && (
            <div className="pt-4 border-t space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Suscripción activada el {format(new Date(profile.subscription_start_date), "d 'de' MMMM, yyyy", { locale: es })}</span>
              </div>
              {profile.subscription_end_date && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Próxima renovación: {format(new Date(profile.subscription_end_date), "d 'de' MMMM, yyyy", { locale: es })}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Botón de Cancelar Suscripción */}
      {isPremium && (
        <div className="mt-6">
          <CancelSubscriptionDialog 
            userId={user.id} 
            onCanceled={() => {
              // Recargar el perfil del usuario después de cancelar
              window.location.reload();
            }} 
          />
        </div>
      )}
    </div>
  );
};